import React from "react";
import { StyleSheet, View } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { FilterChip } from "./FilterChip";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IShareFilterCriteria } from "common247";

export interface IShareFilterComponentProps {
  criteria: IShareFilterCriteria;
  onChange: (updated: IShareFilterCriteria) => void;
}

const ROLES: ("viewer" | "contributor" | "editor" | "admin")[] = ["viewer", "editor"];

const DIRECTIONS = [
  { key: "all", label: "All Shares" },
  { key: "shared_by_me", label: "Shared by Me" },
  { key: "shared_with_me", label: "Shared with Me" },
];

const SOURCE_TYPES: ("memory" | "media" | "contact" | "group")[] = [
  "memory",
  "media",
  "contact",
  "group",
];

/**
 * Filter controls specific to Shares and access permissions.
 */
export function ShareFilter({
  criteria,
  onChange,
}: IShareFilterComponentProps): React.JSX.Element {
  const textMuted = useThemeColor({}, "textMuted");

  const currentRoles = criteria.roles || [];
  const currentDirection = criteria.direction || "all";
  const currentSourceType = criteria.sourceType;

  const handleRoleToggle = (role: "viewer" | "contributor" | "editor" | "admin"): void => {
    const updated = currentRoles.includes(role)
      ? currentRoles.filter((r) => r !== role)
      : [...currentRoles, role];
    onChange({ ...criteria, roles: updated.length > 0 ? updated : undefined });
  };

  return (
    <View style={styles.container}>
      {/* Direction */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Sharing Direction</ThemedText>
        <View style={styles.chipsWrap}>
          {DIRECTIONS.map((dir) => (
            <FilterChip
              key={dir.key}
              label={dir.label}
              selected={currentDirection === dir.key}
              onPress={() => onChange({ ...criteria, direction: dir.key as any })}
              compact
            />
          ))}
        </View>
      </View>

      {/* Role */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Role / Permission</ThemedText>
        <View style={styles.chipsWrap}>
          {ROLES.map((r) => (
            <FilterChip
              key={r}
              label={r.toUpperCase()}
              selected={currentRoles.includes(r)}
              onPress={() => handleRoleToggle(r)}
              compact
            />
          ))}
        </View>
      </View>

      {/* Source Type */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Item Type</ThemedText>
        <View style={styles.chipsWrap}>
          <FilterChip
            label="All Types"
            selected={!currentSourceType}
            onPress={() => onChange({ ...criteria, sourceType: undefined })}
            compact
          />
          {SOURCE_TYPES.map((type) => (
            <FilterChip
              key={type}
              label={type.toUpperCase()}
              selected={currentSourceType === type}
              onPress={() => onChange({ ...criteria, sourceType: type })}
              compact
            />
          ))}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 4,
  },
  section: {
    marginBottom: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 4,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
});
